/**
 * @file index.tsx
 *
 * @fileoverview Shows the gallery widget content when in view mode.
 */
import React, { useState } from 'react';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { useIntl } from 'react-intl';
import { OrganizationGalleryWidgetViewProps } from '~/jobiqo/components/Organization/Widgets/Gallery/View';
import styles from './galleryContent.module.scss';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { ImageGallery } from '~/vcl/molecules/11-gallery/01-image-gallery';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { AddIcon } from '~/vcl/icons/Add';

export const OrganizationGalleryWidgetViewContent = ({
  organization,
  boxId,
}: OrganizationGalleryWidgetViewProps) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  const intl = useIntl();

  const handleImgClick = (index: number) => {
    setIsGalleryOpen(true);
    setStartIndex(index);
  };

  const galleryLength = organization?.gallery?.length as number;

  // Extracts image properties from organization.
  const OrganizationImageThumbnail = ({
    thumb,
    index,
    children = <></>,
    title = intl.formatMessage({
      id: 'organization_image_gallery_img_title',
      defaultMessage: 'Open gallery',
    }),
  }) => {
    const thumbnail = (organization as any)?.[`gallery_thumb_${thumb}`]?.[
      index
    ];

    return (
      <button onClick={() => handleImgClick(index)} title={title}>
        <img alt={thumbnail.alt} src={thumbnail.url} />
        {children}
      </button>
    );
  };

  return (
    <>
      <FlexItems justifyContent="flex-end">
        <EditLink
          organization={organization}
          boxId={boxId}
          boxHasTitle={true}
        />
      </FlexItems>
      {galleryLength > 0 && (
        <ImageGallery
          isGalleryOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
          startIndex={startIndex}
          images={(organization?.gallery as any)?.map((galleryImage) => {
            return {
              src: galleryImage?.url,
              alt: galleryImage?.alt,
            };
          })}
        />
      )}
      <div className={styles.responsiveGrid}>
        {galleryLength > 0 && (
          <Cell gridColumn={['span 2']} gridRow={[1, 'span 2']}>
            <OrganizationImageThumbnail index={0} thumb={1} />
          </Cell>
        )}
        {galleryLength > 1 && (
          <Cell gridColumn={[1, 3]} gridRow={['span 2']}>
            <OrganizationImageThumbnail index={1} thumb={3} />
          </Cell>
        )}
        {galleryLength > 2 && (
          <Cell gridColumn={[2, 4]} gridRow={['span 2']}>
            <OrganizationImageThumbnail index={2} thumb={3} />
          </Cell>
        )}
        {galleryLength > 3 && (
          <Cell gridColumn={[1, 5]} gridRow={['span 2']}>
            <OrganizationImageThumbnail index={3} thumb={3} />
          </Cell>
        )}
        {galleryLength > 4 && (
          <Cell gridColumn={[2, 6]} gridRow={['auto', 1]}>
            <OrganizationImageThumbnail index={4} thumb={2} />
          </Cell>
        )}
        {galleryLength > 5 && (
          <Cell gridColumn={[2, 6]} gridRow={['auto', 2]}>
            <div className={styles.showMoreThumbnail}>
              <OrganizationImageThumbnail
                index={5}
                thumb={2}
                title={intl.formatMessage({
                  id: 'organization_image_gallery_show_more',
                  defaultMessage: 'Show more',
                })}
              >
                <FlexItems justifyContent="center" alignItems="center">
                  <AddIcon /> {galleryLength - 5}
                </FlexItems>
              </OrganizationImageThumbnail>
            </div>
          </Cell>
        )}
      </div>
    </>
  );
};
export default OrganizationGalleryWidgetViewContent;
